import numeral from 'numeral'
import { ORDER_TYPE, PROFIT_LOSS_RATE, TIME_IN_FORCE } from 'const/trade'
import getFixedPriceWithMinTick from 'utils/getFixedPriceWithMinTick'
import getPriceMinTick from 'utils/getPriceMinTick'
import { MARKET_TYPE, SEC_TYPE } from 'const/stock'

// 监听订单修改信息
export default {
  computed: {
    orderType() {
      return this.order.orderType
    },
    action() {
      return this.order.action
    },
    isLMT() {
      return this.orderType === ORDER_TYPE.LMT
    },
    isSTP() {
      return this.orderType === ORDER_TYPE.STP
    },
    isTRAIL1() {
      return this.orderType === ORDER_TYPE.TRAIL_1
    },
    isTRAIL2() {
      return this.orderType === ORDER_TYPE.TRAIL_2
    },
    isSTPAndLMT() {
      return this.orderType === ORDER_TYPE.STP_LMT
    },
    isAddPROFIT() {
      return (
        this.orderType === ORDER_TYPE.LMT &&
        [ORDER_TYPE.BRACKETS, ORDER_TYPE.PROFIT].includes(
          this.order.attachOrderType
        )
      )
    },
    isAddLOSS() {
      return (
        this.orderType === ORDER_TYPE.LMT &&
        [ORDER_TYPE.BRACKETS, ORDER_TYPE.LOSS].includes(
          this.order.attachOrderType
        )
      )
    }
  },
  watch: {
    orderType() {
      const params = {
        attachOrderType: ''
      }
      if (this.isLMT) {
        params.outsideRth = this.getDefaultRth()
        params.limitPrice = this.getLimitPrice()
      }
      if (this.isSTP) {
        params.stpPrice = this.getStpPrice()
      }
      if (this.isSTPAndLMT) {
        params.stpPrice = this.getStpPrice()
        params.limitPrice = this.getStpLimit()
      }
      if (this.isTRAIL1) {
        params.trailingPercent = this.getTrailPercent()
      }
      if (this.isTRAIL2) {
        params.trailingPrice = this.getTrailPrice()
      }
      if (!this.isLMT && !this.isSTPAndLMT) {
        params.limitPrice = ''
        params.outsideRth = false
      }
      if (!this.isSTP && !this.isSTPAndLMT) {
        params.stpPrice = ''
      }
      this.$store.dispatch('updateTradeOrder', params)
    },
    // 修改买卖方向止损价（止损限价）需要更新（包含附加单止损止盈价）快捷仓位更新
    action() {
      const params = { quickPosition: null }
      if (this.isSTP) {
        params.stpPrice = this.getStpPrice()
      }
      if (this.isSTPAndLMT) {
        params.stpPrice = this.getStpPrice()
        params.limitPrice = this.getStpLimit()
      }
      if (this.isAddPROFIT) {
        params.profitTakerPrice = this.getAddProfitPrice()
      }
      if (this.isAddLOSS) {
        params.stopLossPrice = this.getAddLossPrice()
      }
      this.$store.dispatch('updateTradeOrder', params)
    },
    // 止盈单
    isAddPROFIT(isAddPROFIT) {
      if (isAddPROFIT) {
        this.$store.dispatch('updateTradeOrder', {
          profitTakerPrice: this.getAddProfitPrice(),
          profitTakerTif: TIME_IN_FORCE.DAY,
          profitTakerRth: false
        })
      }
    },
    // 止损单
    isAddLOSS() {
      if (this.isAddLOSS) {
        this.$store.dispatch('updateTradeOrder', {
          stopLossPrice: this.getAddLossPrice(),
          stopLossTif: TIME_IN_FORCE.DAY,
          stopLossRth: false
        })
      }
    }
  },
  methods: {
    getTrailPercent() {
      return (PROFIT_LOSS_RATE.MAIN * 100).toFixed(2)
    },
    getTrailPrice() {
      const latestPrice = this.getLatestPrice()
      const minTick = getPriceMinTick(latestPrice, this.minTicks)
      const price = numeral(latestPrice)
        .multiply(PROFIT_LOSS_RATE.MAIN)
        .value()
      return getFixedPriceWithMinTick(price, minTick, true)
    },
    // 获取限价
    getLimitPrice() {
      const latestPrice = this.getLatestPrice()
      const minTick = getPriceMinTick(latestPrice, this.minTicks)
      return getFixedPriceWithMinTick(latestPrice, minTick, true)
    },
    // 获取止损价
    getStpPrice() {
      const latestPrice = this.getLatestPrice()
      const minTick = getPriceMinTick(latestPrice, this.minTicks)
      const stpPrice =
        this.action === 'BUY'
          ? numeral(latestPrice)
              .multiply(1 + PROFIT_LOSS_RATE.MAIN)
              .value()
          : numeral(latestPrice)
              .multiply(1 - PROFIT_LOSS_RATE.MAIN)
              .value()
      return getFixedPriceWithMinTick(stpPrice, minTick, true)
    },
    // 获取止损限价
    getStpLimit() {
      const latestPrice = this.getLatestPrice()
      const minTick = getPriceMinTick(latestPrice, this.minTicks)
      const limitPrice =
        this.action === 'BUY'
          ? numeral(latestPrice)
              .multiply(1 + PROFIT_LOSS_RATE.MAIN)
              .add(minTick)
              .value()
          : numeral(latestPrice)
              .multiply(1 - PROFIT_LOSS_RATE.MAIN)
              .subtract(minTick)
              .value()
      return getFixedPriceWithMinTick(limitPrice, minTick, true)
    },
    // 获取附加止盈单价格
    getAddProfitPrice() {
      const minTick = getPriceMinTick(this.order.limitPrice, this.minTicks)
      const profitTakerPrice =
        this.action === 'BUY'
          ? numeral(this.order.limitPrice)
              .multiply(1 + PROFIT_LOSS_RATE.ATTACH_PROFIT)
              .value()
          : numeral(this.order.limitPrice)
              .divide(1 + PROFIT_LOSS_RATE.ATTACH_PROFIT)
              .value()

      return getFixedPriceWithMinTick(profitTakerPrice, minTick, true)
    },
    // 获取附加止损单价格
    getAddLossPrice() {
      const minTick = getPriceMinTick(this.order.limitPrice, this.minTicks)
      const stopLossPrice =
        this.action === 'BUY'
          ? numeral(this.order.limitPrice)
              .multiply(1 - PROFIT_LOSS_RATE.ATTACH_LOSS)
              .value()
          : numeral(this.order.limitPrice)
              .divide(1 - PROFIT_LOSS_RATE.ATTACH_LOSS)
              .value()

      return getFixedPriceWithMinTick(stopLossPrice, minTick, true)
    },
    // 获取最新价
    getLatestPrice() {
      return this.stock.hourTradingLatestPrice || this.stock.latestPrice
    },
    // 盘前盘后成交（美股正股限价单允许）
    getDefaultRth() {
      return (
        this.orderType === ORDER_TYPE.LMT &&
        this.order.market === MARKET_TYPE.US &&
        this.order.secType === SEC_TYPE.STK
      )
    }
  }
}
